import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { PlusCircle, Search, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

export default function DailyAttendanceListing() {
  const nav = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // login context (same pattern you used)
  const finYearId = Number(localStorage.getItem("finYearId") || 0);
  const branchId = Number(localStorage.getItem("branchId") || 0);
  const branchType = String(localStorage.getItem("branchtype") || "HO"); // HO / BRANCH

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // dropdowns
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [showFilters, setShowFilters] = useState(true);

  // filters (ASP fields)
  const [empName, setEmpName] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [designation, setDesignation] = useState("Select");
  const [branch, setBranch] = useState("Select");
  const [department, setDepartment] = useState("Select");

  // UI search + paging
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // delete selection
  const [selected, setSelected] = useState(new Set());

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const inputBase =
    `w-full px-4 py-2 rounded-lg border outline-none transition-all focus:ring-2 focus:ring-blue-500 focus:border-transparent ` +
    (isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500");

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const loadMeta = async () => {
    if (!finYearId) return toast.warn("Please select Financial Year.");
    if (!branchId) return toast.warn("branchId missing (login).");

    try {
      const res = await axios.get(`${BASE_URL}/api/payroll/daily-attendance/meta`, {
        params: { finYearId, branchId, branchType },
      });

      if (!res?.data?.success) throw new Error(res?.data?.message || "Meta failed");

      setBranches(res.data.branches || []);
      setDesignations(res.data.designations || []);
      setDepartments(res.data.departments || []);

      // default branch selection = login branch (like ASP)
      setBranch(String(branchId));
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load dropdowns");
    }
  };

  const loadList = async () => {
    if (!finYearId) return toast.warn("Please select Financial Year.");
    if (!branchId) return toast.warn("branchId missing (login).");

    try {
      setLoading(true);
      setSelected(new Set());

      const res = await axios.get(`${BASE_URL}/api/payroll/daily-attendance/list`, {
        params: {
          finYearId,
          branchId,
          branchType,
          empCode: empCode.trim(),
          empName: empName.trim(),
          designation: designation !== "Select" ? designation : "",
          branch: branch !== "Select" ? branch : "",
          department: department !== "Select" ? department : "%",
        },
      });

      if (!res?.data?.success) throw new Error(res?.data?.message || "List failed");
      setRows(res?.data?.rows || []);
      setPage(1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta().then(loadList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddNew = () => {
    localStorage.removeItem("DailyID");
    nav("/payroll/DailyAttendanceEntry/entry");
  };

  const onView = (row) => {
    const empId = Number(row.fld_employeeid || row.fld_employeeId || 0);
    if (!empId) return toast.error("Employee ID missing");
    nav(`/payroll/DailyAttendanceEntry/edit/${empId}`);
  };

  const onViewAll = async () => {
    setEmpName("");
    setEmpCode("");
    setDesignation("Select");
    setDepartment("Select");
    // keep login branch selected like ASP
    setBranch(String(branchId));
    setSearch("");
    setPage(1);
    await loadList();
  };

  // client-side text search (extra like your request earlier)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const b = (r.fld_BranchName ?? "").toString().toLowerCase();
      const name = (r.fld_Employee ?? "").toString().toLowerCase();
      const code = (r.fld_Employeecode ?? "").toString().toLowerCase();
      const type = (r.fld_typename ?? "").toString().toLowerCase();
      const desig = (r.fld_designationname ?? "").toString().toLowerCase();
      const date = (r.fld_date ?? r.dailydate ?? "").toString().toLowerCase();
      return (
        b.includes(q) ||
        name.includes(q) ||
        code.includes(q) ||
        type.includes(q) ||
        desig.includes(q) ||
        date.includes(q)
      );
    });
  }, [rows, search]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / pageSize)), [filtered.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const toggleOne = (dailyId) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(dailyId)) n.delete(dailyId);
      else n.add(dailyId);
      return n;
    });
  };

  const toggleAllOnPage = (checked) => {
    setSelected((prev) => {
      const n = new Set(prev);
      for (const r of paged) {
        const id = Number(r.fld_DailyAttendance_ID || r.fld_employeeid || 0);
        if (!id) continue;
        if (checked) n.add(id);
        else n.delete(id);
      }
      return n;
    });
  };

  const allOnPageSelected =
    paged.length > 0 &&
    paged.every((r) => selected.has(Number(r.fld_DailyAttendance_ID || r.fld_employeeid)));

  const onDelete = async () => {
    if (selected.size === 0) return toast.warn("Please select any one");
    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    try {
      setLoading(true);

      // send items with empcode + date so backend can replicate leaveOB clean logic safely
      const items = Array.from(selected).map((id) => {
        const row = rows.find((r) => Number(r.fld_DailyAttendance_ID || r.fld_employeeid) === Number(id));
        return {
          dailyId: id,
          employeeCode: row?.fld_Employeecode || row?.fld_employeecode || "",
          date: row?.fld_date || row?.dailydate || row?.fld_Date || "", // if SP returns date
        };
      });

      const res = await axios.post(`${BASE_URL}/api/payroll/daily-attendance/delete`, {
        finYearId,
        items,
      });

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Deleted");
        await loadList();
      } else {
        toast.error(res?.data?.message || "Delete failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DAILY ATTENDANCE LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Daily Attendance Listing
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={onAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <PlusCircle className="w-4 h-4" />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={onViewAll}
                    disabled={loading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      loading
                        ? "opacity-60 cursor-not-allowed"
                        : isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <RefreshCw className="w-4 h-4" />
                    View All
                  </button>

                  <button
                    onClick={loadList}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <Search className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Employee Name</label>
                  <input value={empName} onChange={(e) => setEmpName(e.target.value)} className={inputBase} />
                </div>

                <div>
                  <label className={labelClass}>Employee Code</label>
                  <input value={empCode} onChange={(e) => setEmpCode(e.target.value)} className={inputBase} />
                </div>

                <div>
                  <label className={labelClass}>Designation</label>
                  <select value={designation} onChange={(e) => setDesignation(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {designations.map((d) => (
                      <option key={d.fld_desigid} value={d.fld_designationname}>
                        {d.fld_designationname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select value={branch} onChange={(e) => setBranch(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {branches.map((b, idx) => {
                      const val = String(
                        b.Value ?? b.fld_Branchid ?? b.fld_branchid1 ?? b.fld_branchId ?? b.fld_branchID ?? b.value ?? ""
                      );
                      const txt = String(b.Text ?? b.fld_BranchName ?? b.fld_branchName ?? b.text ?? val);
                      if (!val) return null;
                      return (
                        <option key={`${val}-${idx}`} value={val}>
                          {txt}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Department</label>
                  <select value={department} onChange={(e) => setDepartment(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {departments.map((d) => (
                      <option key={d.fld_deptid} value={String(d.fld_deptid)}>
                        {d.fld_departmentname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== TABLE TOOLBAR (in-table search + page size) ==================== */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className={inputBase + " pl-9"}
                placeholder="Search in table..."
              />
            </div>

            <select
              className={inputBase + " sm:w-40"}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full min-w-[1200px] ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Branch</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Employee Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Employee Code</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Employee Type</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Designation</th>

                  {/* SELECT ALL - LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={(e) => toggleAllOnPage(e.target.checked)}
                      className="w-4 h-4 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loading ? (
                  <tr>
                    <td colSpan={7} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No Records Found!
                    </td>
                  </tr>
                ) : (
                  paged.map((r, idx) => {
                    const dailyId = Number(r.fld_DailyAttendance_ID || r.fld_employeeid || 0);
                    const checked = selected.has(dailyId);

                    return (
                      <tr
                        key={`${dailyId}-${idx}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_BranchName ?? "-"}
                        </td>

                        {/* Employee name is link (ASP ButtonField View) */}
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => onView(r)}
                            className={`font-semibold underline ${
                              isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"
                            }`}
                          >
                            {r.fld_employee ?? "-"}
                          </button>
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeecode ?? "-"}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_typename ?? "-"}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_designationname ?? "-"}
                        </td>

                        {/* SELECT */}
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(dailyId)}
                            disabled={!dailyId}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* ==================== REMOVE BUTTON ==================== */}
            <div className="p-4">
              <button
                onClick={onDelete}
                disabled={loading || selected.size === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                  loading || selected.size === 0 ? "opacity-50 cursor-not-allowed" : ""
                } bg-red-600 hover:bg-red-700 text-white`}
              >
                🗑 Remove
              </button>
            </div>
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div
            className={`flex flex-wrap items-center justify-between gap-3 mt-4 p-4 rounded-lg border ${
              isDark ? "border-gray-700 bg-gray-800/30" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing {filtered.length === 0 ? 0 : (page - 1) * pageSize + 1}-
              {Math.min(page * pageSize, filtered.length)} of {filtered.length}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className={`px-3 py-1 rounded border text-sm ${
                  page === 1 ? "opacity-50 cursor-not-allowed" : "hover:shadow"
                } ${isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"}`}
              >
                {"<<"}
              </button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`px-3 py-1 rounded border text-sm ${
                  page === 1 ? "opacity-50 cursor-not-allowed" : "hover:shadow"
                } ${isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"}`}
              >
                {"<"}
              </button>

              <span className={`text-sm px-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                Page <b>{page}</b> / <b>{totalPages}</b>
              </span>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={`px-3 py-1 rounded border text-sm ${
                  page === totalPages ? "opacity-50 cursor-not-allowed" : "hover:shadow"
                } ${isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"}`}
              >
                {">"}
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className={`px-3 py-1 rounded border text-sm ${
                  page === totalPages ? "opacity-50 cursor-not-allowed" : "hover:shadow"
                } ${isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"}`}
              >
                {">>"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
}